import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersAPI as userAPI, type User, type UserChangePassword, type UserResetPassword } from '../services';
import { useAuth } from '../context/AuthContext';

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const isSuperAdmin = currentUser?.UserType === "01";
  const isOwnPassword = currentUser?.UserID === userId;

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const userData = await userAPI.getUserById(userId);
        setUser(userData);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));

    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) return;

    // Validation
    if (formData.new_password !== formData.confirm_password) {
      setError(t('changePassword.passwordMismatch'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (formData.new_password.length < 6) {
      setError(t('changePassword.minLength'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // For own password, current password is required
    if (isOwnPassword && !formData.current_password) {
      setError(t('changePassword.currentRequired'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isSuperAdmin && !isOwnPassword) {
        // Super admin resetting another user's password
        const resetData: UserResetPassword = {
          new_password: formData.new_password,
          confirm_password: formData.confirm_password
        };
        await userAPI.resetPassword(userId, resetData);
        setSuccess(t('changePassword.resetSuccess'));
        // Scroll to top to show success alert
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // User changing own password or super admin changing own password
        const changeData: UserChangePassword = {
          current_password: formData.current_password,
          new_password: formData.new_password,
          confirm_password: formData.confirm_password
        };
        await userAPI.changePassword(userId, changeData);
        setSuccess(t('changePassword.changeSuccess'));
        // Scroll to top to show success alert
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      // Clear form
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

    } catch (err: any) {
      setError(err?.response?.data?.detail || t('changePassword.changeFailed'));
      // Scroll to top to show error alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/users/${userId}/edit`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('common.userNotFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          {t('changePassword.backToUser')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isSuperAdmin && !isOwnPassword
              ? t('changePassword.resetTitle', { name: user.full_name || user.UserName })
              : t('changePassword.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {isSuperAdmin && !isOwnPassword
              ? t('changePassword.adminNote')
              : t('changePassword.userNote')
            }
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box component="form" onSubmit={handleSubmit}>
            {/* Current Password - only show if changing own password */}
            {isOwnPassword && (
              <TextField
                label={t('changePassword.currentPassword')}
                type="password"
                value={formData.current_password}
                onChange={handleChange('current_password')}
                fullWidth
                required
                margin="normal"
                autoComplete="current-password"
              />
            )}

            <TextField
              label={t('changePassword.newPassword')}
              type="password"
              value={formData.new_password}
              onChange={handleChange('new_password')}
              fullWidth
              required
              margin="normal"
              autoComplete="new-password"
              helperText={t('changePassword.minLength')}
            />

            <TextField
              label={t('changePassword.confirmPassword')}
              type="password"
              value={formData.confirm_password}
              onChange={handleChange('confirm_password')}
              fullWidth
              required
              margin="normal"
              autoComplete="new-password"
            />

            <Box mt={3} display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? t('common.saving') : (isSuperAdmin && !isOwnPassword ? t('changePassword.resetPassword') : t('changePassword.changePassword'))}
              </Button>

              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePassword;
